import { useState } from 'react';
import { useMutation } from '@apollo/client';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import DeleteIcon from '@mui/icons-material/Delete';
import ButtonGroup from 'react-bootstrap/ButtonGroup';

import ELEMENT from 'common/element';
import { AlertInfo } from 'common/graphqlTypes';

import Button from 'components/common/Button/Button';
import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';
import Toolbar from 'components/common/Toolbar/Toolbar';
import CheckboxRadio from 'components/common/CheckboxRadio/CheckboxRadio';
import ConfirmDialog from 'components/common/Dialog/ConfirmDialog';
import SilenceAlertsButton, { SilenceAlertInput } from 'components/alerts/SilenceAlertsButton';
import { AlertModel } from 'components/alerts/AlertListPage/useAlerts';
import useAlertPageAlerts from 'components/alerts/useAlertPageAlerts';
import { createMuteCondition, getNonTemplatedAlerts } from 'components/alerts/alert';
import {
  ALERTS_QUERY,
  DELETE_ALERTS_MUTATION,
  SILENCE_ALERTS_MUTATION,
  UNSILENCE_ALERTS_MUTATION,
} from 'components/alerts/queries';

import './AlertListToolbar.scss';

/**
 * AlertsListToolbar is a toolbar with buttons that allows the user to interact with alerts.
 *
 * @prop {AlertModel} alertModel       The AlertModel that manages alert states
 * @prop {string} className            Additional class(es) for this components.
 */

type Props = {
  alertModel: AlertModel;
  className: string;
};

function AlertListToolbar({ alertModel, className }: Props): JSX.Element {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const refetchQueries = [{ query: ALERTS_QUERY }];
  const [silenceAlerts] = useMutation(SILENCE_ALERTS_MUTATION, { refetchQueries });
  const [unSilenceAlerts] = useMutation(UNSILENCE_ALERTS_MUTATION, { refetchQueries });
  const [deleteAlerts] = useMutation(DELETE_ALERTS_MUTATION, { refetchQueries });
  const alertPageAlerts = useAlertPageAlerts();

  const handleMuteAlert = (silenceInputs: SilenceAlertInput): Promise<void> => {
    const { silenceComment, silenceUntil, selectedOption } = silenceInputs;
    const silenceConditions = alertModel.getSelectedAlerts().map((alert: AlertInfo) => ({
      alertId: alert.alertId,
      condition: createMuteCondition(alert.templateParameters),
    }));

    return silenceAlerts({
      variables: {
        silenceComment,
        silenceConditions,
        silenceUntil: silenceUntil || selectedOption,
      },
    })
      .then(alertModel.unselectAlerts)
      .then(alertPageAlerts.showMuteSuccess)
      .catch(alertPageAlerts.showError);
  };

  const handleUnMuteAlerts = (): void => {
    const ids = alertModel.getSelectedAlerts().map((alert: AlertInfo) => alert.alertId);
    unSilenceAlerts({ variables: { ids } })
      .then(alertModel.unselectAlerts)
      .then(alertPageAlerts.showUnMuteSuccess)
      .catch(alertPageAlerts.showError);
  };

  const handleDeleteAlerts = (): void => {
    const ids = getNonTemplatedAlerts(alertModel.getSelectedAlerts()).map((alert: AlertInfo) => alert.alertId);
    deleteAlerts({ variables: { ids } })
      .then(alertModel.unselectAlerts)
      .then(alertPageAlerts.showDeleteSuccess)
      .catch(alertPageAlerts.showError);
  };

  const handleShowConfirmDeleteDialog = (): void => {
    setShowConfirmDialog(true);
  };

  const handleHideConfirmDeleteDialog = (): void => {
    setShowConfirmDialog(false);
  };

  const isSelectAllCheckboxDisabled = (): boolean => {
    if (alertModel.isCurrentlyOnTriggeredTab) {
      return alertModel.triggeredAlerts.length === 0;
    }

    if (alertModel.isCurrentlyOnMutedTab) {
      return alertModel.mutedAlerts.length === 0;
    }

    if (alertModel.isCurrentlyOnIncompleteTab) {
      return alertModel.incompleteAlerts.length === 0;
    }

    return alertModel.alerts.length === 0;
  };

  const isSelectAllCheckboxChecked = (): boolean => {
    if (alertModel.isCurrentlyOnTriggeredTab) {
      return alertModel.hasSelectedAllTriggeredAlerts;
    }

    if (alertModel.isCurrentlyOnMutedTab) {
      return alertModel.hasSelectedAllMutedAlerts;
    }

    if (alertModel.isCurrentlyOnIncompleteTab) {
      return alertModel.hasSelectedAllIncompleteAlerts;
    }

    return alertModel.hasSelectedAllAlerts;
  };

  const handleSelectAllCheckboxClick = (): void => {
    if (alertModel.isCurrentlyOnTriggeredTab) {
      alertModel.handleSelectAllTriggeredAlerts();
    } else if (alertModel.isCurrentlyOnMutedTab) {
      alertModel.handleSelectAllMutedAlerts();
    } else if (alertModel.isCurrentlyOnIncompleteTab) {
      alertModel.handleSelectAllIncompleteAlerts();
    } else if (alertModel.isCurrentlyOnAllTab) {
      alertModel.handleSelectAllAlerts();
    }
  };

  const leftItems = [
    <CheckboxRadio
      className="AlertListToolbar__CheckboxInput"
      onChange={handleSelectAllCheckboxClick}
      checked={isSelectAllCheckboxChecked()}
      disabled={isSelectAllCheckboxDisabled()}
      testId={ELEMENT.alertSelectAllCheckbox}
    />,
    <ButtonGroup>
      <Button
        disabled={!alertModel.hasSelectedAtLeastOneAlert}
        onClick={handleUnMuteAlerts}
        size="sm"
        leftIcon={<VolumeUpIcon fontSize="small" />}
        testId={ELEMENT.alertUnmuteButton}
      >
        Unmute
      </Button>
      <SilenceAlertsButton
        size="sm"
        disabled={!alertModel.hasSelectedAtLeastOneAlert}
        submitForm={handleMuteAlert}
        testId={ELEMENT.alertMuteButton}
      />
    </ButtonGroup>,
    <div className="AlertListToolbar__DeleteButtonContainer">
      {alertModel.hasSelectedTemplatedAlerts && (
        <ElementWithTooltip placement="auto" tooltip="Cannot delete templated alerts">
          <div className="AlertListToolbar__DeleteButtonTooltip" />
        </ElementWithTooltip>
      )}
      <Button
        disabled={!alertModel.hasSelectedAtLeastOneAlert || alertModel.hasSelectedTemplatedAlerts}
        onClick={handleShowConfirmDeleteDialog}
        size="sm"
        leftIcon={<DeleteIcon fontSize="small" />}
        testId={ELEMENT.alertDeleteButton}
      >
        Delete
      </Button>
    </div>,
  ];

  return (
    <div className={className}>
      <Toolbar leftItems={leftItems} />
      <ConfirmDialog
        title="Confirm Delete Alert(s)"
        text="Are you sure you want to delete the selected alert(s)?"
        show={showConfirmDialog}
        close={handleHideConfirmDeleteDialog}
        onSubmit={handleDeleteAlerts}
        cannotBeUndone={true}
      />
    </div>
  );
}

export default AlertListToolbar;
